import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Check, ArrowRight, PlayCircle, Tag, Palette, Car } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductsPage = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();

  // Car selection state
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [bodyType, setBodyType] = useState('');
  const [isOtherBrand, setIsOtherBrand] = useState(false);
  
  // Product selection state
  const [selectedProduct, setSelectedProduct] = useState('');
  const [includeInstallationKit, setIncludeInstallationKit] = useState(false);
  const [selectedColour, setSelectedColour] = useState('');
  const [colours, setColours] = useState([]);
  
  // Custom car request modal
  const [showCustomCarModal, setShowCustomCarModal] = useState(false);
  const [customCarForm, setCustomCarForm] = useState({
    name: '',
    email: '',
    phone: '',
    car_brand: '',
    car_model: '',
    car_year: '',
    body_type: 'sedan',
    product_type: 'gloss',
    colour: '',
    include_installation_kit: false,
    message: ''
  });
  const [submittingCustom, setSubmittingCustom] = useState(false);
  
  // Loading states
  const [loading, setLoading] = useState(false);

  // Pricing with $500 PROMO discount
  const PRICING = {
    large: { gloss: 2400, matt: 2700, coloured: 3100, essential: 650 },
    standard: { gloss: 1900, matt: 2200, coloured: 2600, essential: 550 }
  };
  const ORIGINAL_PRICING = {
    large: { gloss: 2900, matt: 3200, coloured: 3600, essential: 650 },
    standard: { gloss: 2400, matt: 2700, coloured: 3100, essential: 550 }
  };
  const INSTALLATION_KIT_PRICE = 100;
  const SHIPPING_PRICE = 35;

  const isLargeVehicle = ['ute', 'suv', 'van'].includes(bodyType?.toLowerCase());
  const priceCategory = isLargeVehicle ? 'large' : 'standard';

  // Fetch brands and colours on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [brandsRes, coloursRes] = await Promise.all([
          axios.get(`${API}/cars/brands`),
          axios.get(`${API}/colours`)
        ]);
        setBrands(brandsRes.data.brands);
        setColours(coloursRes.data.colours);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data');
      }
    };
    fetchData();
  }, []);

  // Fetch models when brand changes
  useEffect(() => {
    if (selectedBrand) {
      if (selectedBrand === 'Other') {
        setIsOtherBrand(true);
        setShowCustomCarModal(true);
        setModels([]);
        setYears([]);
        setBodyType('');
        setSelectedModel('');
        setSelectedYear('');
      } else {
        setIsOtherBrand(false);
        const fetchModels = async () => {
          try {
            const response = await axios.get(`${API}/cars/models/${selectedBrand}`);
            setModels(response.data.models);
            setSelectedModel('');
            setSelectedYear('');
            setBodyType('');
          } catch (error) {
            console.error('Error fetching models:', error);
          }
        };
        fetchModels();
      }
    }
  }, [selectedBrand]);

  // Fetch years and body type when model changes
  useEffect(() => {
    if (selectedBrand && selectedModel && !isOtherBrand) {
      const fetchYearsAndBodyType = async () => {
        try {
          const [yearsRes, bodyTypeRes] = await Promise.all([
            axios.get(`${API}/cars/years/${selectedBrand}`),
            axios.get(`${API}/cars/body-type/${selectedBrand}/${selectedModel}`)
          ]);
          setYears(yearsRes.data.years);
          setBodyType(bodyTypeRes.data.body_type);
          setSelectedYear('');
        } catch (error) {
          console.error('Error fetching years/body type:', error);
        }
      };
      fetchYearsAndBodyType();
    }
  }, [selectedBrand, selectedModel, isOtherBrand]);

  const products = [
    {
      id: 'gloss',
      name: 'Whole Body Gloss Film',
      description: 'Premium Vetrox gloss PPF for complete vehicle coverage. Self-healing technology with crystal clear protection.',
      image: 'https://images.pexels.com/photos/14231700/pexels-photo-14231700.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
      hasPromo: true
    },
    {
      id: 'matt',
      name: 'Whole Body Matt Film',
      description: 'Premium Vetrox matt finish PPF for a sleek, modern satin look. Self-healing technology included.',
      image: 'https://images.pexels.com/photos/32726112/pexels-photo-32726112.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
      hasPromo: true
    },
    {
      id: 'coloured',
      name: 'Whole Body Coloured Film',
      description: 'Transform your vehicle with Vetrox coloured PPF. Wide range of colours available for a complete transformation.',
      image: 'https://images.pexels.com/photos/10162529/pexels-photo-10162529.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
      hasPromo: true,
      hasColourSelection: true
    },
    {
      id: 'essential',
      name: 'Essential Paint Protection Gloss Package',
      description: 'Protect high-impact areas including bonnet, front bumper, headlights and mirrors.',
      image: 'https://images.pexels.com/photos/10126665/pexels-photo-10126665.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
      hasPromo: false
    }
  ];

  // Group colours by category
  const coloursByCategory = colours.reduce((acc, colour) => {
    if (!acc[colour.category]) acc[colour.category] = [];
    acc[colour.category].push(colour);
    return acc;
  }, {});

  const calculateTotal = useCallback(() => {
    if (!selectedProduct || !bodyType) return 0;
    let total = PRICING[priceCategory][selectedProduct] || 0;
    if (includeInstallationKit) total += INSTALLATION_KIT_PRICE;
    total += SHIPPING_PRICE;
    return total;
  }, [selectedProduct, bodyType, includeInstallationKit, priceCategory]);

  const handleAddToCart = (goToCheckout = true) => {
    if (!selectedBrand || !selectedModel || !selectedYear || !selectedProduct) {
      toast.error('Please complete all selections');
      return;
    }
    
    if (selectedProduct === 'coloured' && !selectedColour) {
      toast.error('Please select a colour for your coloured film');
      return;
    }

    const colourName = selectedProduct === 'coloured' 
      ? colours.find(c => c.id === selectedColour)?.name 
      : null;

    const cartItem = {
      car: {
        brand: selectedBrand,
        model: selectedModel,
        year: selectedYear,
        body_type: bodyType
      },
      product: {
        product_type: selectedProduct,
        include_installation_kit: includeInstallationKit,
        name: products.find(p => p.id === selectedProduct)?.name,
        colour: selectedColour,
        colour_name: colourName
      },
      pricing: {
        product_price: PRICING[priceCategory][selectedProduct],
        original_price: ORIGINAL_PRICING[priceCategory][selectedProduct],
        discount: selectedProduct !== 'essential' ? 500 : 0,
        installation_kit_price: includeInstallationKit ? INSTALLATION_KIT_PRICE : 0,
        shipping_price: SHIPPING_PRICE,
        total_price: calculateTotal()
      }
    };

    addToCart(cartItem);
    toast.success('Added to cart!');
    
    // Reset form for another product
    setSelectedBrand('');
    setSelectedModel('');
    setSelectedYear('');
    setBodyType('');
    setSelectedProduct('');
    setSelectedColour('');
    setIncludeInstallationKit(false);
    
    if (goToCheckout) {
      navigate('/checkout');
    }
  };

  const handleCustomCarSubmit = async () => {
    if (!customCarForm.name || !customCarForm.email || !customCarForm.phone || 
        !customCarForm.car_brand || !customCarForm.car_model || !customCarForm.car_year) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmittingCustom(true);
    try {
      await axios.post(`${API}/custom-car-request`, customCarForm);
      toast.success('Request submitted! Our team will contact you shortly.');
      setShowCustomCarModal(false);
      setSelectedBrand('');
      setCustomCarForm({
        name: '', email: '', phone: '', car_brand: '', car_model: '', car_year: '',
        body_type: 'sedan', product_type: 'gloss', colour: '', include_installation_kit: false, message: ''
      });
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmittingCustom(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen pt-24 bg-[#050505]">
      {/* Promo Banner */}
      <div className="bg-gradient-to-r from-[#00F0FF]/20 to-[#00F0FF]/5 border-b border-[#00F0FF]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-center space-x-2">
            <Tag className="w-5 h-5 text-[#00F0FF]" />
            <p className="text-[#00F0FF] font-bold text-sm sm:text-base">
              LIMITED TIME: $500 OFF all Whole Body PPF Kits!
            </p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="bg-[#0A0A0A] border-b border-[#222222]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00F0FF] mb-2">Build Your Kit</p>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-medium text-[#F8F8F8]">
            Configure Your PPF Kit
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left side - Configuration */}
          <div className="lg:col-span-2 space-y-10">
            {/* Step 1: Car Selection */}
            <div className="bg-[#111111] border border-[#222222] p-6 md:p-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className={`step-indicator ${selectedBrand && !isOtherBrand ? 'completed' : 'active'}`}>1</div>
                <h2 className="text-xl font-medium text-[#F8F8F8]">Select Your Vehicle</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-[#A0A0A0] mb-2">Brand</label>
                  <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                    <SelectTrigger className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-12 rounded-none" data-testid="select-brand">
                      <SelectValue placeholder="Select brand" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#333333] max-h-[300px]">
                      {brands.map(brand => (
                        <SelectItem key={brand} value={brand} className="text-[#F8F8F8]">
                          {brand === 'Other' ? (
                            <span className="flex items-center space-x-2">
                              <Car className="w-4 h-4" />
                              <span>Other (Can't find my car)</span>
                            </span>
                          ) : brand}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="block text-sm text-[#A0A0A0] mb-2">Model</label>
                  <Select 
                    value={selectedModel} 
                    onValueChange={setSelectedModel}
                    disabled={!selectedBrand || isOtherBrand}
                  >
                    <SelectTrigger className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-12 rounded-none disabled:opacity-50" data-testid="select-model">
                      <SelectValue placeholder={isOtherBrand ? "Use custom form" : "Select model"} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#333333] max-h-[300px]">
                      {models.map(model => (
                        <SelectItem key={model} value={model} className="text-[#F8F8F8]">{model}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="block text-sm text-[#A0A0A0] mb-2">Year</label>
                  <Select 
                    value={selectedYear} 
                    onValueChange={setSelectedYear}
                    disabled={!selectedModel || isOtherBrand}
                  >
                    <SelectTrigger className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-12 rounded-none disabled:opacity-50" data-testid="select-year">
                      <SelectValue placeholder={isOtherBrand ? "Use custom form" : "Select year"} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#333333]">
                      {years.map(year => (
                        <SelectItem key={year} value={year} className="text-[#F8F8F8]">{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {bodyType && !isOtherBrand && (
                <div className="mt-4 p-3 bg-[#1A1A1A] border border-[#333333]">
                  <p className="text-sm text-[#A0A0A0]">
                    Vehicle Type: <span className="text-[#00F0FF] font-medium uppercase">{bodyType}</span>
                    {isLargeVehicle ? ' (UTE/SUV/VAN pricing)' : ' (Sedan/Station/Mini pricing)'}
                  </p>
                </div>
              )}

              {isOtherBrand && (
                <div className="mt-4 p-4 bg-[#1A1A1A] border border-[#00F0FF]/30">
                  <p className="text-[#A0A0A0] text-sm mb-3">
                    Can't find your vehicle? No problem! Fill out our custom request form and we'll contact you.
                  </p>
                  <Button
                    onClick={() => setShowCustomCarModal(true)}
                    className="bg-[#00F0FF] text-[#050505] font-bold uppercase tracking-wide hover:bg-[#33F3FF] px-6 py-2 rounded-none"
                    data-testid="open-custom-form-btn"
                  >
                    Open Custom Request Form
                  </Button>
                </div>
              )}
            </div>

            {/* Step 2: Product Selection */}
            <div className="bg-[#111111] border border-[#222222] p-6 md:p-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className={`step-indicator ${selectedProduct ? 'completed' : selectedYear ? 'active' : 'inactive'}`}>2</div>
                <h2 className="text-xl font-medium text-[#F8F8F8]">Choose Your Film</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {products.map((product) => {
                  const price = bodyType ? PRICING[priceCategory][product.id] : null;
                  const originalPrice = bodyType ? ORIGINAL_PRICING[priceCategory][product.id] : null;
                  const isSelected = selectedProduct === product.id;
                  
                  return (
                    <div
                      key={product.id}
                      onClick={() => {
                        if (bodyType) {
                          setSelectedProduct(product.id);
                          if (!product.hasColourSelection) setSelectedColour('');
                        }
                      }}
                      className={`radio-card p-4 ${isSelected ? 'selected' : ''} ${!bodyType ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid={`product-card-${product.id}`}
                    >
                      <div className="aspect-[16/9] mb-4 overflow-hidden relative">
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                        {product.hasPromo && (
                          <div className="absolute top-2 right-2 bg-[#FF3B30] text-white text-xs font-bold px-2 py-1">
                            SAVE $500
                          </div>
                        )}
                      </div>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-[#F8F8F8] font-medium mb-1">{product.name}</h3>
                          <p className="text-[#666666] text-sm leading-relaxed">{product.description}</p>
                        </div>
                        {isSelected && (
                          <div className="w-6 h-6 bg-[#00F0FF] rounded-full flex items-center justify-center flex-shrink-0 ml-3">
                            <Check className="w-4 h-4 text-[#050505]" />
                          </div>
                        )}
                      </div>
                      {price !== null && (
                        <div className="mt-3 flex items-center space-x-2">
                          <p className="text-[#00F0FF] font-bold text-lg">{formatPrice(price)}</p>
                          {product.hasPromo && originalPrice !== price && (
                            <p className="text-[#666666] line-through text-sm">{formatPrice(originalPrice)}</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Step 2.5: Colour Selection (for coloured film) */}
            {selectedProduct === 'coloured' && (
              <div className="bg-[#111111] border border-[#222222] p-6 md:p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <Palette className="w-6 h-6 text-[#00F0FF]" />
                  <h2 className="text-xl font-medium text-[#F8F8F8]">Select Your Colour</h2>
                </div>
                
                {Object.entries(coloursByCategory).map(([category, categoryColours]) => (
                  <div key={category} className="mb-6">
                    <h3 className="text-sm text-[#A0A0A0] mb-3 uppercase tracking-wide">{category}</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                      {categoryColours.map((colour) => (
                        <button
                          key={colour.id}
                          onClick={() => setSelectedColour(colour.id)}
                          className={`p-3 text-left text-sm transition-all ${
                            selectedColour === colour.id 
                              ? 'bg-[#1A1A1A] border-[#00F0FF] text-[#F8F8F8]' 
                              : 'bg-[#0A0A0A] border-[#222222] text-[#A0A0A0] hover:border-[#444444]'
                          } border`}
                          data-testid={`colour-${colour.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="truncate">{colour.name}</span>
                            {selectedColour === colour.id && (
                              <Check className="w-4 h-4 text-[#00F0FF] flex-shrink-0 ml-2" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Step 3: Add-ons */}
            <div className="bg-[#111111] border border-[#222222] p-6 md:p-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className={`step-indicator ${selectedProduct ? 'active' : 'inactive'}`}>3</div>
                <h2 className="text-xl font-medium text-[#F8F8F8]">Add-ons (Optional)</h2>
              </div>
              
              <div
                onClick={() => selectedProduct && setIncludeInstallationKit(!includeInstallationKit)}
                className={`checkbox-card p-6 flex items-start space-x-4 ${includeInstallationKit ? 'selected' : ''} ${!selectedProduct ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="installation-kit-checkbox"
              >
                <Checkbox 
                  checked={includeInstallationKit}
                  onCheckedChange={setIncludeInstallationKit}
                  disabled={!selectedProduct}
                  className="mt-1 border-[#444444] data-[state=checked]:bg-[#00F0FF] data-[state=checked]:border-[#00F0FF]"
                />
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-[#F8F8F8] font-medium">Installation Kit</h3>
                      <p className="text-[#666666] text-sm mt-1">
                        Includes installation gel, pressure spray, spray bottle, blade, squeegee and comprehensive tutorial videos.
                      </p>
                    </div>
                    <p className="text-[#00F0FF] font-bold ml-4">+{formatPrice(INSTALLATION_KIT_PRICE)}</p>
                  </div>
                  <div className="flex items-center space-x-2 mt-3">
                    <PlayCircle className="w-4 h-4 text-[#00F0FF]" />
                    <span className="text-sm text-[#A0A0A0]">Tutorial videos included</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-28">
              <div className="bg-[#111111] border border-[#222222] p-6">
                <h3 className="text-lg font-medium text-[#F8F8F8] mb-6">Order Summary</h3>
                
                {selectedBrand && selectedModel && selectedYear && (
                  <div className="mb-6 pb-6 border-b border-[#222222]">
                    <p className="text-sm text-[#A0A0A0] mb-1">Vehicle</p>
                    <p className="text-[#F8F8F8] font-medium">
                      {selectedYear} {selectedBrand} {selectedModel}
                    </p>
                    <p className="text-xs text-[#666666] uppercase mt-1">{bodyType}</p>
                  </div>
                )}

                {selectedProduct && (
                  <div className="mb-6 pb-6 border-b border-[#222222]">
                    <p className="text-sm text-[#A0A0A0] mb-1">Product</p>
                    <p className="text-[#F8F8F8] font-medium">
                      {products.find(p => p.id === selectedProduct)?.name}
                    </p>
                    {selectedProduct === 'coloured' && selectedColour && (
                      <p className="text-[#00F0FF] text-sm mt-1">
                        Colour: {colours.find(c => c.id === selectedColour)?.name}
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-3 mb-6">
                  {selectedProduct && (
                    <div className="flex justify-between items-center">
                      <span className="text-[#A0A0A0]">Film</span>
                      <div className="text-right">
                        <span className="text-[#F8F8F8]">{formatPrice(PRICING[priceCategory][selectedProduct])}</span>
                        {selectedProduct !== 'essential' && (
                          <span className="text-[#666666] line-through text-sm ml-2">
                            {formatPrice(ORIGINAL_PRICING[priceCategory][selectedProduct])}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {selectedProduct && selectedProduct !== 'essential' && (
                    <div className="flex justify-between text-[#34C759]">
                      <span>Promo Discount</span>
                      <span>-$500</span>
                    </div>
                  )}
                  {includeInstallationKit && (
                    <div className="flex justify-between">
                      <span className="text-[#A0A0A0]">Installation Kit</span>
                      <span className="text-[#F8F8F8]">{formatPrice(INSTALLATION_KIT_PRICE)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-[#A0A0A0]">Shipping (Australia Post)</span>
                    <span className="text-[#F8F8F8]">{formatPrice(SHIPPING_PRICE)}</span>
                  </div>
                </div>

                <div className="flex justify-between items-center py-4 border-t border-[#222222]">
                  <span className="text-[#F8F8F8] font-medium">Total</span>
                  <span className="text-[#00F0FF] text-2xl font-bold">{formatPrice(calculateTotal())}</span>
                </div>

                <div className="space-y-2 mt-4">
                  <Button
                    onClick={() => handleAddToCart(true)}
                    disabled={!selectedBrand || !selectedModel || !selectedYear || !selectedProduct || (selectedProduct === 'coloured' && !selectedColour)}
                    className="w-full bg-[#00F0FF] text-[#050505] font-bold uppercase tracking-wide hover:bg-[#33F3FF] py-6 rounded-none disabled:bg-[#333333] disabled:text-[#666666]"
                    data-testid="add-to-cart-btn"
                  >
                    Add & Checkout
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  
                  <Button
                    onClick={() => handleAddToCart(false)}
                    disabled={!selectedBrand || !selectedModel || !selectedYear || !selectedProduct || (selectedProduct === 'coloured' && !selectedColour)}
                    variant="outline"
                    className="w-full border-[#444444] text-[#F8F8F8] hover:bg-[#1A1A1A] py-4 rounded-none disabled:opacity-50"
                    data-testid="add-another-btn"
                  >
                    Add & Continue Shopping
                  </Button>
                </div>

                <div className="flex items-center justify-center space-x-2 mt-4">
                  <Shield className="w-4 h-4 text-[#34C759]" />
                  <span className="text-sm text-[#A0A0A0]">10 Year Warranty Included</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Car Request Modal */}
      <Dialog open={showCustomCarModal} onOpenChange={setShowCustomCarModal}>
        <DialogContent className="bg-[#111111] border-[#222222] text-[#F8F8F8] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">Custom Vehicle Request</DialogTitle>
            <DialogDescription className="text-[#A0A0A0]">
              Can't find your vehicle? Fill in the details below and we'll contact you with a custom quote.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#A0A0A0]">Your Name *</Label>
                <Input
                  value={customCarForm.name}
                  onChange={(e) => setCustomCarForm(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-10 rounded-none mt-1"
                  data-testid="custom-name"
                />
              </div>
              <div>
                <Label className="text-[#A0A0A0]">Phone *</Label>
                <Input
                  value={customCarForm.phone}
                  onChange={(e) => setCustomCarForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-10 rounded-none mt-1"
                  data-testid="custom-phone"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-[#A0A0A0]">Email *</Label>
              <Input
                type="email"
                value={customCarForm.email}
                onChange={(e) => setCustomCarForm(prev => ({ ...prev, email: e.target.value }))}
                className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-10 rounded-none mt-1"
                data-testid="custom-email"
              />
            </div>

            <div className="border-t border-[#222222] pt-4">
              <h4 className="text-[#F8F8F8] font-medium mb-3">Vehicle Details</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-[#A0A0A0]">Brand *</Label>
                  <Input
                    value={customCarForm.car_brand}
                    onChange={(e) => setCustomCarForm(prev => ({ ...prev, car_brand: e.target.value }))}
                    placeholder="e.g. Geely"
                    className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-10 rounded-none mt-1"
                    data-testid="custom-car-brand"
                  />
                </div>
                <div>
                  <Label className="text-[#A0A0A0]">Model *</Label>
                  <Input
                    value={customCarForm.car_model}
                    onChange={(e) => setCustomCarForm(prev => ({ ...prev, car_model: e.target.value }))}
                    placeholder="e.g. Coolray"
                    className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-10 rounded-none mt-1"
                    data-testid="custom-car-model"
                  />
                </div>
                <div>
                  <Label className="text-[#A0A0A0]">Year *</Label>
                  <Input
                    value={customCarForm.car_year}
                    onChange={(e) => setCustomCarForm(prev => ({ ...prev, car_year: e.target.value }))}
                    placeholder="e.g. 2024"
                    className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-10 rounded-none mt-1"
                    data-testid="custom-car-year"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-[#A0A0A0]">Body Type</Label>
              <Select 
                value={customCarForm.body_type} 
                onValueChange={(value) => setCustomCarForm(prev => ({ ...prev, body_type: value }))}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-10 rounded-none mt-1" data-testid="custom-body-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#333333]">
                  <SelectItem value="sedan" className="text-[#F8F8F8]">Sedan</SelectItem>
                  <SelectItem value="suv" className="text-[#F8F8F8]">SUV</SelectItem>
                  <SelectItem value="ute" className="text-[#F8F8F8]">UTE</SelectItem>
                  <SelectItem value="van" className="text-[#F8F8F8]">Van</SelectItem>
                  <SelectItem value="station" className="text-[#F8F8F8]">Station Wagon</SelectItem>
                  <SelectItem value="mini" className="text-[#F8F8F8]">Mini/Hatchback</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[#A0A0A0]">Product Interested In</Label>
              <Select 
                value={customCarForm.product_type} 
                onValueChange={(value) => setCustomCarForm(prev => ({ ...prev, product_type: value }))}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-10 rounded-none mt-1" data-testid="custom-product">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#333333]">
                  <SelectItem value="gloss" className="text-[#F8F8F8]">Whole Body Gloss Film</SelectItem>
                  <SelectItem value="matt" className="text-[#F8F8F8]">Whole Body Matt Film</SelectItem>
                  <SelectItem value="coloured" className="text-[#F8F8F8]">Whole Body Coloured Film</SelectItem>
                  <SelectItem value="essential" className="text-[#F8F8F8]">Essential Package</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {customCarForm.product_type === 'coloured' && (
              <div>
                <Label className="text-[#A0A0A0]">Preferred Colour (Optional)</Label>
                <Input
                  value={customCarForm.colour}
                  onChange={(e) => setCustomCarForm(prev => ({ ...prev, colour: e.target.value }))}
                  placeholder="e.g. Miami Blue, Ferrari Red"
                  className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-10 rounded-none mt-1"
                  data-testid="custom-colour"
                />
              </div>
            )}

            <div className="flex items-center space-x-3">
              <Checkbox
                checked={customCarForm.include_installation_kit}
                onCheckedChange={(checked) => setCustomCarForm(prev => ({ ...prev, include_installation_kit: checked }))}
                className="border-[#444444] data-[state=checked]:bg-[#00F0FF] data-[state=checked]:border-[#00F0FF]"
              />
              <span className="text-sm text-[#A0A0A0]">Include Installation Kit (+$100)</span>
            </div>

            <div>
              <Label className="text-[#A0A0A0]">Additional Notes</Label>
              <Textarea
                value={customCarForm.message}
                onChange={(e) => setCustomCarForm(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Any specific requirements or questions..."
                className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] rounded-none mt-1 resize-none"
                rows={3}
                data-testid="custom-message"
              />
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                onClick={() => {
                  setShowCustomCarModal(false);
                  setSelectedBrand('');
                }}
                variant="outline"
                className="flex-1 border-[#444444] text-[#F8F8F8] hover:bg-[#1A1A1A] py-5 rounded-none"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCustomCarSubmit}
                disabled={submittingCustom}
                className="flex-1 bg-[#00F0FF] text-[#050505] font-bold uppercase tracking-wide hover:bg-[#33F3FF] py-5 rounded-none"
                data-testid="submit-custom-request-btn"
              >
                {submittingCustom ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;
